import React, { useState } from 'react';
import { Star } from 'lucide-react';

interface FeedbackFormProps {
  analysisId: number;
  onSubmitted?: () => void;
}

export function FeedbackForm({ analysisId, onSubmitted }: FeedbackFormProps) {
  const [rating, setRating] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/analyses/${analysisId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, notes: notes || null }),
      });

      if (response.ok) {
        setSubmitted(true);
        if (onSubmitted) onSubmitted();
        setTimeout(() => {
          setSubmitted(false);
          setRating(0);
          setNotes('');
        }, 3000);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to submit feedback');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="feedback-form"
      style={{
        padding: 'var(--space-4)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        backgroundColor: 'var(--color-bg-secondary)',
        marginTop: 'var(--space-4)',
      }}
    >
      <label
        className="t-label-sm"
        style={{ marginBottom: 'var(--space-3)', display: 'block', fontWeight: 600 }}
      >
        How helpful was this analysis?
      </label>

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => {
              setRating(star);
              setError(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 'var(--space-1)',
              transition: 'transform 0.1s',
              transform: star <= rating ? 'scale(1.1)' : 'scale(1)',
            }}
            title={`${star} star${star !== 1 ? 's' : ''}`}
          >
            <Star
              size={24}
              fill={star <= rating ? 'var(--color-warning)' : 'transparent'}
              stroke={star <= rating ? 'var(--color-warning)' : 'var(--color-border-strong)'}
              strokeWidth={1.75}
            />
          </button>
        ))}
      </div>

      <textarea
        placeholder="Optional: What could improve this analysis? (max 1000 characters)"
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setError(null);
        }}
        maxLength={1000}
        style={{
          width: '100%',
          minHeight: '80px',
          padding: 'var(--space-2)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-sm)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-sm-size)',
          marginBottom: 'var(--space-3)',
          color: 'var(--color-text-primary)',
          backgroundColor: 'var(--color-background)',
        }}
      />

      {error && (
        <p style={{ color: 'var(--color-danger)', marginBottom: 'var(--space-2)', fontSize: 'var(--text-body-sm-size)' }}>
          {error}
        </p>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <button
          onClick={handleSubmit}
          disabled={rating === 0 || loading || submitted}
          className={`btn ${submitted ? 'btn-success' : 'btn-primary'} btn-sm`}
          style={{ opacity: rating === 0 ? 0.5 : 1 }}
        >
          {loading ? 'Submitting…' : submitted ? '✓ Submitted' : 'Submit Feedback'}
        </button>
        {submitted && (
          <span style={{ color: 'var(--color-success)', fontSize: 'var(--text-body-sm-size)' }}>
            Thank you! Your feedback helps improve future analyses.
          </span>
        )}
      </div>
    </div>
  );
}
